"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Icon from "@/components/ui/AppIcon";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const pathname = usePathname();

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "dark" | "light" | null;
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.toggle("light", savedTheme === "light");
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.classList.toggle("light", newTheme === "light");
  };

  const navLinks = [
    { label: "What We Do", href: "/services", id: "nav_services" },
    { label: "Integrations by Process", href: "/services#processes", id: "nav_processes" },
    { label: "Integration Types", href: "/services#types", id: "nav_types" },
    { label: "ERP & Platforms", href: "/homepage#erp", id: "nav_erp" },
    { label: "Industries & Standards", href: "/homepage#standards", id: "nav_standards" },
    { label: "How We Engage", href: "/services#engage", id: "nav_engage" },
    { label: "Research & Reference", href: "#research", id: "nav_research" },
    { label: "Partner Architects", href: "/partners", id: "nav_partners" },
    { label: "Start Here", href: "/wizard", id: "nav_wizard" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-neutral-950/80 backdrop-blur-md border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3">
          <span className="text-xl font-bold text-white">e2eint</span>
          <span className="hidden md:block text-sm text-neutral-400">
            End-to-End Integrations. Designed to Deliver.
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6 text-sm">
          {navLinks.slice(0, 9).map((link) => (
            <Link
              key={link.id}
              href={link.href}
              className={`transition-colors ${
                pathname === link.href || pathname.startsWith(link.href.split("#")[0])
                  ? "text-blue-400" :"text-neutral-400 hover:text-white"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTAs + Theme Picker */}
        <div className="flex items-center gap-4">
          {/* Theme Picker */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            aria-label="Toggle theme"
          >
            <Icon
              name={theme === "dark" ? "SunIcon" : "MoonIcon"}
              size={20}
              className="text-white"
            />
          </button>

          {/* Request Demo */}
          <Link
            href="/contact?topic=demo"
            className="hidden md:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-sm font-medium hover:scale-105 transition-transform"
          >
            Request Demo
          </Link>

          {/* Contact */}
          <Link
            href="/contact"
            className="hidden md:flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white rounded-full text-sm font-medium hover:bg-white/10 transition-colors"
          >
            Contact
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-full bg-white/5 border border-white/10"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={mobileMenuOpen ? "XMarkIcon" : "Bars3Icon"}
              size={24}
              className="text-white"
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-neutral-900 border-t border-white/5">
          <nav className="flex flex-col px-6 py-4 gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`text-sm transition-colors ${
                  pathname === link.href || pathname.startsWith(link.href.split("#")[0])
                    ? "text-blue-400" :"text-neutral-400"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              href="/contact?topic=demo"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-4 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-sm font-medium text-center"
            >
              Request Demo
            </Link>
            <Link
              href="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="px-4 py-2 bg-white/5 border border-white/10 text-white rounded-full text-sm font-medium text-center"
            >
              Contact
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}